<template>
  <new-modal-dialog
    visible
    persistent
    scrollable
    max-width="800px"
    :title="title"
    :fullscreen="$vuetify.breakpoint.xsOnly"
  >
    <validation-observer ref="observer">
      <v-form ref="form">
        <v-row class="pt-5">
          <v-col
            v-if="isNew"
            cols="12"
            class="pt-0 mt-0"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-select
                v-model="t.locale"
                class="pt-0"
                :items="locales"
                :error-messages="errors"
                :label="$t('label.language')"
                :hint="!t.locale ? $i18n.t('label.hint_mc_specify_language') : null"
                :persistent-hint="!t.locale"
                @input="localeChanged"
              />
            </validation-provider>
          </v-col>

          <template v-if="t.locale">
            <v-col
              v-if="!isSMS"
              cols="12"
              class="pt-0 mt-0"
            >
              <!-- TODO why can subject be undefined? -->
              <validated-text-field
                v-model="t.subject"
                rules="required|max:255"
                :label="$t('label.subject')"
              />
            </v-col>
            <v-col
              cols="12"
              style="min-width: 375px"
              class="pt-0 mt-0"
            >
              <validation-provider
                v-slot="{errors}"
                :rules="isSMS ? 'required|max:603' : 'required'"
              >
                <rich-text-editor
                  v-model="t.body"
                  min-height="40vh"
                  max-height="100%"
                  height="40vh"
                  :richtext-allowed="!isSMS"
                  :error-messages="errors"
                  :hint="isSMS ? smsMessageLength : ''"
                >
                  <template #menubar="{editor}">
                    <v-menu offset-y>
                      <template #activator="{ on }">
                        <v-btn
                          class="px-4 mr-2"
                          color="primary"
                          outlined
                          small
                          v-on="on"
                        >
                          {{ $t('button.insert_placeholder') }}
                        </v-btn>
                      </template>
                      <v-list>
                        <v-list-item
                          v-for="(item, i) in useHandlebarPlaceholders"
                          :key="i"
                          @click="editor?.commands.insertHandlebar({ id: `${item.id}`, label: item.name })"
                        >
                          <v-list-item-title>{{ item.name }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                </rich-text-editor>
              </validation-provider>
            </v-col>
          </template>
        </v-row>
      </v-form>
    </validation-observer>

    <!-- Actions -->
    <template #actions>
      <v-spacer />
      <v-btn
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        {{ $t('button.save') }}
      </v-btn>
      <v-btn
        color="error"
        text
        @click="cancel"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </template>
  </new-modal-dialog>
</template>

<script setup lang="ts">
import {
  computed, onMounted, PropType, Ref, ref,
} from 'vue';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import RichTextEditor from '@/components/editors/RichTextEditor.vue';
import MessageTemplate from '@/model/MessageTemplate';
import Locale from '@/model/Locale';
import MessageRule from '@/model/MessageRule';
import { ValidationObserver } from 'vee-validate';
import ValidatedTextField from '@/components/forms/ValidatedTextField.vue';
import { count } from 'sms-length';
import { MessageTypeChannel } from '@/model/MessageType';
import toast from '@/plugins/toast-plugin/toast';
import { strcmp } from '@/services/common';
import NewModalDialog from '@/components/dialogs/NewModalDialog.vue';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import { useHandlebarPlaceholders } from '@/composables/messagecenter';

const props = defineProps({
  template: { type: Object as PropType<MessageTemplate>, required: true },
  rule: { type: Object as PropType<MessageRule>, required: true },
});

// const tBody = `    <p>This is editable text.</p>
//     <p><span data-type="fixed-block" data-value="summary">Reservation Summary Block</span></p>
//     <p>More editable text.</p>`;

const emit = defineEmits<{(e: 'close'): void; (e:'update-template', event: MessageTemplate): void}>();

const t = ref(cloneModel(props.template));
const oldt = ref(cloneModel(props.template));

const locales = computed(() => {
  const map = new Set<string>();
  props.rule.templates.forEach((o) => { if (!o.isDefault) map.add(o.locale ?? ''); });
  return store.state.codes.locales
    .filter((o) => !map.has(o.locale))
    .sort((a: Locale, b: Locale) => strcmp(a.text, b.text));
});

const isChanged = computed(() => !isModelEqualToModel(t.value, oldt.value));
const isNew = computed(() => props.template.locale === undefined);

const title = computed(() => {
  const lang = (props.template.locale && store.getters.localesByLocale.get(props.template.locale)?.language);
  return [props.rule.eventType?.text, lang].filter((f) => f !== undefined).join(': ');
});

const isSMS = computed(() => props.rule.messageType?.channel === MessageTypeChannel.SMS);

const smsMessageLength = computed(() => {
  const c = count(t.value.body || '');
  const remainingChars = Math.min(603, c.characterPerMessage * (c.messages || 1));
  return i18n.t('label.approx_sms_messages', [c.length, remainingChars, c.messages]) as string;
});

const localeChanged = () => {
  const template = props.rule.templates.find((o) => o.locale === t.value.locale) ?? new MessageTemplate();
  if (!template) return;

  t.value.body = template.body;
  t.value.subject = template.subject;
};

onMounted(() => {
  if (!t.value.locale) {
    const { locale } = store.state.settings.account;
    if (!props.rule.templates.find((o) => !o.isDefault && o.locale === locale)) {
      t.value.locale = locale;
      localeChanged();
    }
  }
});

const observer = ref(null) as Ref<null | InstanceType<typeof ValidationObserver>>;
const save = async () => {
  const isValid = await observer.value!.validate();
  if (!isValid) { toast.error(i18n.tc('error.validation_failed')); return; }
  emit('update-template', t.value);
};

const cancel = () => { emit('close'); };

</script>
